import { API_BASE } from '../../../constants'
import { fetchWithPolicy } from '../../../../shared/api/fetchPolicy'
import { normalizePage } from '../../../normalizers'
import { enrichStorefrontPageRuntimeBlocks } from '../../../../cms/storefrontRuntime'
import { dedupeSeoAlternates } from '../../../seo/localePolicy'
import { attachPageSeoAlternates } from '../routeContentLoader.pageSeo'
import type { LoadedRouteContentData, PageRoute } from './types'

const resolvePagePayload = (data: { page?: Record<string, unknown> }): Record<string, unknown> => {
  if (!data.page || typeof data.page !== 'object' || Array.isArray(data.page)) {
    throw new Error('Pagina non trovata')
  }
  return data.page
}

export const fetchPageRouteResponse = ({
  route,
  signal,
  locale,
}: {
  route: PageRoute
  signal: AbortSignal
  locale: string
}) => {
  const endpoint =
    route.mode === 'preview'
      ? `${API_BASE}/pages/${encodeURIComponent(route.id)}/preview`
      : `${API_BASE}/pages/${encodeURIComponent(route.slug)}?locale=${encodeURIComponent(locale)}`
  return fetchWithPolicy({
    request: endpoint,
    init: { signal, cache: 'no-store' },
    retries: 0,
  })
}

export const buildPreviewPageContent = ({
  route,
  data,
}: {
  route: Extract<PageRoute, { mode: 'preview' }>
  data: { page?: Record<string, unknown> }
}): Promise<LoadedRouteContentData> => {
  const page = normalizePage(resolvePagePayload(data))
  page.seoCanonicalLocale = page.locale
  page.seoAlternates = dedupeSeoAlternates(
    [{ locale: page.locale, path: `/preview/${encodeURIComponent(route.id)}` }],
    `/preview/${encodeURIComponent(route.id)}`,
  )
  return enrichStorefrontPageRuntimeBlocks({
    page,
    context: {
      locale: page.locale,
      pageId: page._id,
      pageSlug: `/preview/${route.id}`,
    },
  }).then((runtimePage) => ({ type: 'page', page: runtimePage }))
}

export const buildLocalizedPageContent = async ({
  data,
  signal,
}: {
  data: { page?: Record<string, unknown> }
  signal: AbortSignal
}): Promise<LoadedRouteContentData> => {
  const page = normalizePage(resolvePagePayload(data))
  page.seoCanonicalLocale = page.locale
  await attachPageSeoAlternates({ page, signal })
  const runtimePage = await enrichStorefrontPageRuntimeBlocks({
    page,
    context: {
      locale: page.locale,
      pageId: page._id,
      pageSlug: page.slug,
    },
  })
  return { type: 'page', page: runtimePage }
}
